import { Injectable, OnDestroy } from '@angular/core';
import { Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {  MatDialog, MatDialogConfig,  MatDialogRef } from '@angular/material/dialog';
import { saveAs } from 'file-saver';
import { cloneDeep, difference } from 'lodash-es';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CompanySearch, UniversalSearchModel, UniversalSearchModelType } from '../../core/models';
import { OauthProvider } from '../../oauth/providers';
import { CompanyService } from '../../results/companies/services';
import { ContactsService } from '../../results/contacts/services/contacts.service';
import { RequestBodyService } from './request-body.service';
import { DynamicsComponent } from '../components/export/dynamics/dynamics.component';
import { HubspotComponent } from '../components/export/hubspot/hubspot.component';
import { MarketoComponent } from '../components/export/marketo/marketo.component';
import { OutreachComponent } from '../components/export/outreach/outreach.component';
import { SalesforceComponent } from '../components/export/salesforce/salesforce.component';
import { SalesloftComponent } from '../components/export/salesloft/salesloft.component';
import { ZohoComponent } from '../components/export/zoho/zoho.component';
import { PromptModalComponent } from '../components/prompt-modal/prompt-modal.component';
import { OauthService, OauthWindowStatusResult } from './oauth.service';
import { RoDs } from './rods.service';
import { SalesforceService } from './crm';
import { SavedSearches } from './saved-searches.service';
import { NetworkManagerService } from './network-manager.service';
import { CriteriaToRequestService } from './criteria-to-request.service';
import { SearchService } from '../../results/search/services/search.service';
import {
  AccountAttributesEnum,
  ChurnZeroAttributeNamesEnum,
  FeatureFlagsEnum,
  OfflineExportsExportTypesEnum,
  REQUEST_TYPE,
  ResultTabsetUrlsEnum,
  SidebarTabTypeEnum,
} from '../enums';
import { OfflineExportsApiService } from './api';
import { AccountService } from './account.service';
import { RequestResearchModalComponent } from '../components/request-research-modal/request-research-modal.component';
import {
  AccountExportAppWizardComponent,
} from '../components';
import { AccountIntentTopic, OfflineExportsExportRequestDto, WebhookRequestBody } from '../dto';
import { LEGACY_THEME_CLASS, ModalNavigationEnum } from '../constants';
import { ResultsService } from './results.service';
import { UserService, AuthenticationService } from '../../core/services';
import { RoutableModalsStateService } from './routable-modals-state.service';
import { SearchPanelStructureService } from './search-panel-structure.service';
import { FeatureFlagService } from './feature-flag.service';
import { ExportAllCrmParamsInterface, ExportCompanyAllCrmParamsInterface, ExportCsvPreviewParamsInterface } from '../models';
import { ExportCompanyComponentFactoryService } from './export-company-component-factory.service';
import { getErrorMessage } from '../helpers';
import { SiErrorHandler } from './si-error-handler.service';
import { RumService } from './rum.service';

declare const IntegrateChurnZero: any;


type SavedExportFlowData = {
  method: 'exportCompanyAllCRM' | 'exportAllCRM',
  params: ExportCompanyAllCrmParamsInterface | ExportAllCrmParamsInterface
};

@Injectable({
    providedIn: 'root',
})
export class CommonUtilService implements OnDestroy {
    churnZero = new IntegrateChurnZero();
    loggedInUserData;
    hasRODCredit: boolean = false;
    hasRODCreditsRemaining: boolean = false;
    subscribers: any = {};
    isCRMOpen: boolean = false;
    ifFromList;
    searchModelData: any = cloneDeep(UniversalSearchModel);
    emailPattern = /^((([a-z]|\d|[!#\$%&‘\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&‘\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;
    isSearchFromExecuteives: boolean = false;
    private savedExportFlowData: SavedExportFlowData = null;

    private readonly dateFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(
      'en-US',
      {year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false}
    );

    private readonly isAutomationAlertViewSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);

    // Subject to emit when side navigation should be closed
    private readonly closeSidenavSubject: Subject<void> = new Subject<void>();

    private destroyed: Subject<void> = new Subject<void>();

    constructor(
        private toastr: ToastrService,
        private auth: AuthenticationService,
        private oauthService: OauthService,
        private salesforceService: SalesforceService,
        private companyService: CompanyService,
        private contactsService: ContactsService,
        private saveSearch: SavedSearches,
        private requestBodyService: RequestBodyService,
        private router: Router,
        private rodsService: RoDs,
        public networkManager: NetworkManagerService,
        public logger: SiErrorHandler,
        public rum: RumService,
        public criteriaToRequestService: CriteriaToRequestService,
        private searchService: SearchService,
        private readonly offlineExportsApiService: OfflineExportsApiService,
        private accountService: AccountService,
        private readonly resultsService: ResultsService,
        private readonly userService: UserService,
        private readonly routableModalsStateService: RoutableModalsStateService,
        private structureService: SearchPanelStructureService,
        private readonly featureFlagService: FeatureFlagService,
        private readonly matDialog: MatDialog,
        private readonly exportCompanyComponentFactoryService: ExportCompanyComponentFactoryService,
    ) {
        this.subscribers = this.oauthService.authWindowStatus$
        .pipe(takeUntil(this.destroyed))
        .subscribe((status: OauthWindowStatusResult) => {
            if (status.success && status.provider !== null) {
                this.continueExportFlow();
            }
        });

        this.auth.currentUser$.subscribe((data) => {
            if (data) {
                this.loggedInUserData = data;
                if (
                    this.loggedInUserData &&
                    Array.isArray(this.loggedInUserData.service_credits)
                ) {
                    this.loggedInUserData.service_credits.filter((credit) => {
                        if (credit && credit.credit_type === 'RoD') {
                            this.hasRODCredit = true;
                            if (this.getRodRemainingCredits() > 0) {
                                this.hasRODCreditsRemaining = true;
                            }
                        }
                    });
                }
                // to prevent duplicates in churnzero, we should use email as unique identifier for churnzero users
                // we should use account_id as unique identifier for churnzero accounts
                this.churnZero.setAppKeyChurnZero(
                    environment.churnZeroAppKey,
                    data['account_id'],
                    data['email']
                );
            }
        });

        this.auth.czEvent$.subscribe((event) => {
            if (event) {
                this.churnZeroEvents(event);
            }
        });

        this.oauthService.getExportContinueEvent()
            .subscribe(() => {
                this.continueExportFlow();
            }
        );
    }

    get isAutomationAlertView(): Observable<boolean> {
        return this.isAutomationAlertViewSubject.asObservable();
    }

    /**
     * Observable that emits when sidenav should be closed
     */
    getCloseSidenav(): Observable<void> {
        return this.closeSidenavSubject.asObservable();
    }

    /**
     * Emit event to close sidenav
     */
    closeSidenav(): void {
        this.closeSidenavSubject.next();
    }

    continueExportFlow() {
        if (this.savedExportFlowData && this.savedExportFlowData.method && this.savedExportFlowData.params) {
            this[this.savedExportFlowData.method](this.savedExportFlowData.params as any);
        }
    }

    disableAutomationAlertView(): void {
        this.isAutomationAlertViewSubject.next(false);
    }

    clearSidebarSearch(): void {
        this.requestBodyService.clearSearchSidebar.next(true);
        this.searchService.universalCount = null;
        this.saveSearch.currentSearchId = '';
        this.saveSearch.universalActiveSearch = null;
        this.saveSearch.welComeLoadedFlag.next(false);
        this.requestBodyService.changeCompanyBodyData(undefined);
        this.requestBodyService.changeContactBodyData(undefined);
        this.isAutomationAlertViewSubject.next(false);
        this.searchService.selectedTab = undefined;

        this.searchService.emitSearchPerformedEvent(false, SidebarTabTypeEnum.SALES_INTEL);
        this.structureService.emitCurrentFilterEvent("", SidebarTabTypeEnum.SALES_INTEL);
        this.searchService.searchModelData.location_country_codes=['US'];

        this.clearOriginalCompanyData()
    }

    clearOriginalCompanyData() {
        this.companyService.originalCompany = null;
        this.companyService.setOriginalResultPage(null);
        this.contactsService.setOriginalResultPage(null);
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.unsubscribe();
        if (this.subscribers) {
            this.subscribers.unsubscribe();
        }
    }

    churnZeroEvents(eventName) {
        this.churnZero.trackEventChurnZero(eventName);
    }

    churnZeroAccountAttribute<T = any>(attr: ChurnZeroAttributeNamesEnum, value: T) {
        this.churnZero.setAccountAttributeChurnZero(attr, value);
    }

    isValidUrl(string) {
        if (!string.includes('.')) {
            return false;
        }
        if (!(string.startsWith('https://') || string.startsWith('http://'))) {
            string = 'https://' + string;
        }
        try {
            new URL(string);
            return true;
        } catch {
            return false;
        }
    }

    checkCompanyDomainsAndNames(arr) {
        let companyNames = false;
        let companyDomains = false;
        for (let i = 0; i < arr.length; i++) {
            if (this.isValidUrl(arr[i])) {
                companyDomains = true;
            } else {
                companyNames = true;
            }
            if (companyDomains && companyNames) {
                break;
            }
        }
        return { companyNames, companyDomains };
    }

    accuracyTierChurnZero(tabName, requestPayload) {
        if (!this.userService.hasAccess(AccountAttributesEnum.ACCURACYTIERS)) {
            return;
        }
        this.churnZero.trackEventChurnZero(`Portal - ${ tabName } - Company Accuracy ${ requestPayload.accuracy_tier_company || 'MV' }`);
        this.churnZero.trackEventChurnZero(`Portal - ${ tabName } - Contact Accuracy ${ requestPayload.accuracy_tier_contact || 'EV' }`);
    }

    churnZeroUniversalFilterEvent(tabName, requestPayload) {
        // Company Filter
        // company name or domain
        this.churnZero.trackEventChurnZero('Portal - ' + tabName + ' - Search');
        if (requestPayload.company_ids) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - Company - Name'
                    : 'Portal - ' + tabName + ' - Company Name'
            );
        }
        if (requestPayload.company_domains) {
            const typesOfStrings = this.checkCompanyDomainsAndNames(
                requestPayload.company_domains
            );
            if (typesOfStrings.companyNames) {
                this.churnZero.trackEventChurnZero(
                    tabName === 'Company'
                        ? 'Portal - Company - Name'
                        : 'Portal - ' + tabName + ' - Company Name'
                );
            }
            if (typesOfStrings.companyDomains) {
                this.churnZero.trackEventChurnZero(
                    tabName === 'Company'
                        ? 'Portal - Company - Domain'
                        : 'Portal - ' + tabName + ' - Company Domain'
                );
            }
        }

        // FIRMOGRAPHICS
        if (
            requestPayload.company_location_states ||
            requestPayload.company_location_metro ||
            requestPayload.company_location_zipcodes?.length > 0 ||
            requestPayload.is_international ||
            (requestPayload.company_location_hq &&
                !requestPayload.company_location_branch) ||
            (!requestPayload.company_location_hq &&
                requestPayload.company_location_branch) ||
            (requestPayload.location_types?.length !== 4 &&
                (requestPayload.location_types?.includes('hq') > 0 ||
                    requestPayload.location_types?.includes('branch') > 0 ||
                    requestPayload.location_types?.includes('remote') > 0))
        ) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - ' + tabName + ' - Location'
                    : 'Portal - ' + tabName + ' - Company Location'
            );
        }
        if (
            requestPayload.company_location_hq &&
            !requestPayload.company_location_branch
        ) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - Company - HQ Only'
                    : 'Portal - ' + tabName + ' - Company HQ Only'
            );
        }
        if (
            requestPayload.company_location_branch &&
            !requestPayload.company_location_hq
        ) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - Company - Branch Only'
                    : 'Portal - ' + tabName + ' - Company - Branch Only'
            );
        }
        if (requestPayload.company_location_states) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - ' + tabName + ' - State'
                    : 'Portal - ' + tabName + ' - Company State'
            );
        }
        if (requestPayload.company_location_metro?.length) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - ' + tabName + ' - Metro'
                    : 'Portal - ' + tabName + ' - Company Metro'
            );
        }
        if (requestPayload.company_location_zipcodes?.length) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - ' + tabName + ' - Zip'
                    : 'Portal - ' + tabName + ' - Company Zip'
            );
        }
        // asked to do is international
        if (requestPayload.is_international) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - ' + tabName + ' - International'
                    : 'Portal - ' + tabName + ' - Company International'
            );
        }
        if (requestPayload.is_international) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - ' + tabName + ' - Contact International'
                    : 'Portal - ' + tabName + ' - International'
            );
        }
        if (requestPayload.specialties?.length > 0) {
            this.churnZero.trackEventChurnZero('Portal - ' + tabName + ' - Keywords');
        }
        if (
            requestPayload.company_naics_codes?.length > 0 ||
            requestPayload.company_sic_codes ||
            requestPayload.company_naics_codes_any ||
            requestPayload.company_naics_codes_none ||
            requestPayload.company_naics_codes_all ||
            requestPayload.company_sic_codes_any ||
            requestPayload.company_sic_codes_none ||
            requestPayload.company_sic_codes_all
        ) {
            this.churnZero.trackEventChurnZero('Portal - ' + tabName + ' - Industry');
        }

        if (
            requestPayload.company_min_revenue ||
            requestPayload.company_max_revenue
        ) {
            this.churnZero.trackEventChurnZero('Portal - ' + tabName + ' - Revenue');
        }
        if (
            requestPayload.company_min_size?.length > 0 ||
            requestPayload.company_max_size?.length > 0
        ) {
            this.churnZero.trackEventChurnZero(
                'Portal - ' + tabName + ' - Employees / Size'
            );
        }
        if (requestPayload.job_levels) {
            for (const level in requestPayload.job_levels) {
                if (requestPayload.job_levels[level]) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Job Level'
                    );
                    break;
                }
            }
        }
        if (requestPayload.job_departments) {
            for (const department in requestPayload.job_departments) {
                if (requestPayload.job_departments[department]) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Job Department'
                    );

                    break;
                }
            }
        }
        if (requestPayload.job_departments) {
            for (const subDepartment in requestPayload.job_sub_departments) {
                if (requestPayload.job_sub_departments[subDepartment]) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Job Responsibility'
                    );
                    break;
                }
            }
        }
        if (requestPayload.job_title) {
            this.churnZero.trackEventChurnZero(
                'Portal - ' + tabName + ' - Job Title'
            );
        }
        if (
            requestPayload.company_technographic_products?.length > 0 ||
            requestPayload.company_technographic_products_bool?.length > 0
        ) {
            this.churnZero.trackEventChurnZero(
                'Portal - ' + tabName + ' - Technographics Product'
            );
        }
        if (
            requestPayload.company_technographic_vendors?.length > 0 ||
            requestPayload.company_technographic_vendors_bool?.length > 0
        ) {
            this.churnZero.trackEventChurnZero(
                'Portal - ' + tabName + ' - Technographics Vendor'
            );
        }
        if (
            requestPayload.company_technographic_sub_categories?.length > 0 ||
            requestPayload.company_technographic_sub_categories_bool?.length > 0
        ) {
            this.churnZero.trackEventChurnZero(
                'Portal - ' + tabName + ' - Technographics Category'
            );
        }

        const siPrefix: string = requestPayload.si_intent_topic_ids ? 'SI ' : '';

        if (requestPayload.intent_topic_ids?.length || requestPayload.si_intent_topic_ids?.length) {
            this.churnZero.trackEventChurnZero(
                `Portal - ${tabName} - ${siPrefix}Intent Topics`
            );
        }
        if (
            requestPayload.intent_topic_threshold &&
            requestPayload.intent_topic_threshold !== 1
        ) {
            this.churnZero.trackEventChurnZero(
                `Portal - ${tabName} - ${siPrefix}Intent Topic Threshold`
            );
        }
        if (requestPayload.first_name?.length > 0) {
            this.churnZero.trackEventChurnZero(
                'Portal - ' + tabName + ' - First Name'
            );
        }
        if (requestPayload.last_name?.length > 0) {
            this.churnZero.trackEventChurnZero(
                'Portal - ' + tabName + ' - Last Name'
            );
        }
        if (requestPayload.email) {
            this.churnZero.trackEventChurnZero('Portal - ' + tabName + ' - Email');
        }

        if (
            requestPayload.contact_location_states ||
            requestPayload.contact_location_metro ||
            requestPayload.contact_location_zipcodes?.length > 0 ||
            requestPayload.is_international ||
            (requestPayload.contact_location_hq &&
                !requestPayload.contact_location_branch &&
                !requestPayload.contact_location_remote) ||
            (!requestPayload.contact_location_hq &&
                requestPayload.contact_location_branch &&
                !requestPayload.contact_location_remote) ||
            (requestPayload.contact_location_remote &&
                !requestPayload.contact_location_hq &&
                !requestPayload.contact_location_branch) ||
            (requestPayload.location_types?.length !== 4 &&
                (requestPayload.location_types?.includes('hq') > 0 ||
                    requestPayload.location_types?.includes('branch') > 0 ||
                    requestPayload.location_types?.includes('remote') > 0))
        ) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - ' + tabName + ' - Contact Location'
                    : 'Portal - ' + tabName + ' - Location'
            );
        }

        if (requestPayload.location_types?.length !== 4) {
            if (requestPayload.location_types?.includes('hq') > 0) {
                this.churnZero.trackEventChurnZero(
                    tabName === 'Company'
                        ? 'Portal - Company - Contact HQ'
                        : 'Portal - ' + tabName + ' - HQ'
                );
            }
            if (requestPayload.location_types?.includes('branch') > 0) {
                this.churnZero.trackEventChurnZero(
                    tabName === 'Company'
                        ? 'Portal - Company - Contact Branch'
                        : 'Portal - ' + tabName + ' - Branch'
                );
            }
            if (requestPayload.location_types?.includes('remote') > 0) {
                this.churnZero.trackEventChurnZero(
                    tabName === 'Company'
                        ? 'Portal - Company - Contact Remote'
                        : 'Portal - ' + tabName + ' - Remote'
                );
            }
        }

        if (requestPayload.contact_location_states) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - ' + tabName + ' - Contact State'
                    : 'Portal - ' + tabName + ' - State'
            );
        }
        if (requestPayload.contact_location_metro) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - ' + tabName + ' - Contact Metro'
                    : 'Portal - ' + tabName + ' - Metro'
            );
        }
        if (requestPayload.contact_location_zipcodes?.length > 0) {
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - ' + tabName + ' - Contact Zip'
                    : 'Portal - ' + tabName + ' - Zip'
            );
        }

        if (requestPayload.rankings) {
            for (const ranking in requestPayload.rankings) {
                if (requestPayload.rankings[ranking]) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Rankings'
                    );
                    break;
                }
            }
        }

        if (requestPayload.lists?.length > 0) {
            this.churnZero.trackEventChurnZero(
                'Portal - ' + tabName + ' - Your Lists Company'
            );
        }
        if (requestPayload.suppression_lists?.length > 0) {
            this.churnZero.trackEventChurnZero(
                'Portal - ' + tabName + ' - Your Lists Suppression'
            );
        }
        if (requestPayload.required_fields) {
            for (const field of Object.keys(requestPayload.required_fields)) {
                if (field === 'direct_phone' && requestPayload.required_fields[field]) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Include Direct'
                    );
                }
                if (field === 'any_mobile' && requestPayload.required_fields[field]) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Include Mobile'
                    );
                }
                if (
                    field === 'personal_mobile' &&
                    requestPayload.required_fields[field]
                ) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Include Personal'
                    );
                }
                if (field === 'work_mobile' && requestPayload.required_fields[field]) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Include Work Mobile'
                    );
                }
                if (field === 'phone' && requestPayload.required_fields[field]) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Include Phone'
                    );
                }
                if (
                    field === 'personal_email' &&
                    requestPayload.required_fields[field]
                ) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Include Personal Email'
                    );
                }
                if (field === 'address' && requestPayload.required_fields[field]) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Include Address'
                    );
                }
                if (field === 'linkedin_url' && requestPayload.required_fields[field]) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Include Linkedin Url'
                    );
                }
                if (field === 'job_title' && requestPayload.required_fields[field]) {
                    this.churnZero.trackEventChurnZero(
                        'Portal - ' + tabName + ' - Include Title'
                    );
                }
            }
        }

        if (requestPayload.gte_last_modified_date?.length > 0) {
            const date = requestPayload.gte_last_modified_date.split('_')[0];
            const modifiedDate: any = new Date(
                date.slice(0, 4),
                date.slice(4, 6) - 1,
                date.slice(6, 8)
            );
            const todayDate: any = new Date();
            const diffDays: any = Math.floor(
                (todayDate - modifiedDate) / (1000 * 60 * 60 * 24)
            );
            let dayCount;
            if (
                diffDays === 7 ||
                diffDays === 14 ||
                diffDays === 21 ||
                diffDays === 30
            ) {
                dayCount = diffDays;
            } else {
                dayCount = 'Custom';
            }
            this.churnZero.trackEventChurnZero(
                tabName === 'Company'
                    ? 'Portal - Company - Contact - Last Modified ' + dayCount
                    : 'Portal - ' + tabName + ' - Last Modified ' + dayCount
            );
        }

        // Accuracy Tiers:
        this.accuracyTierChurnZero(tabName, requestPayload);
    }

    openRoDModal(companyName?, companyId?) {
        this.churnZero.trackEventChurnZero('Portal - Header - RoD');
        if (this.hasRODCreditsRemaining) {
            const initialState: MatDialogConfig = {
                panelClass: 'modal-md',
                data: {
                    title: 'Research on Demand',
                    labelText:
                        'Provide your research criteria (title, level, department, etc)',
                    saveButtonText: 'Submit',
                    closeButtonText: 'Close',
                    inputValue: '',
                    mandatoryInput: true,
                },
            };
            this.matDialog.open(RequestResearchModalComponent, initialState).afterClosed()
            .subscribe((data: any) => {
                if (data && data.success) {
                    this.companyService.isLoader = true;
                    this.companyService
                    .provideFeedback({
                        from: 'Portal',
                        details:
                            data.inputValue +
                            ':' +
                            (companyName ? companyName : '') +
                            (companyId ? '(cid:' + companyId + ')' : ''),
                        title: data.requestName,
                    })
                    .subscribe(
                        (response) => {
                            this.companyService.isLoader = false;
                            this.rodsService.rodRowData.unshift(response);
                            this.rodsService.setRoDs(this.rodsService.rodRowData);
                            this.toastr.success('Requested successfully!');
                        },
                        () => {
                            this.companyService.isLoader = false;
                        }
                    );
                }
            });
        } else {
            this.displayExhaustedRodMessage();
        }
    }

    public setRevealError(errorStatus: number, errorMessage: string) {
        if (errorStatus === 492 || errorStatus === 426) {
            errorMessage =
                'Your export failed because you reached your personal credit limit. Reach out to your administrator (' +
                this.auth.accountAdmin +
                ') to adjust your limit if you require more credits.';
        } else if (errorStatus === 402 || errorStatus === 406) {
            errorMessage = `You do not have enough credits. Please contact support@salesintel.io.`;
        } else {
            errorMessage = 'Something went wrong: ' + errorMessage;
        }
        this.showToastWithError(errorMessage);
        return errorMessage;
    }

    // Check for undefined and null data
    public getValue(value: any) {
        return value ? value : '';
    }

    purchaseHistory(data) {
        if (
            data.isRevealed ||
            data.isExportedToCsv ||
            data.isExportedToSalesforce ||
            data.isExportedToSalesloft ||
            data.isExportedToHubspot ||
            data.isExportedToOutreach ||
            data.isExportedToMarketo ||
            data.isExportedToDynamics ||
            data.isExportedToZoho
        ) {
            return true;
        } else {
            return false;
        }
    }

    changeKeyname(data) {
        data.map((s) => {
            if (s.hasOwnProperty('companyId')) {
                s.company_id = s.companyId;
                delete s.companyId;
            }
        });
        return data;
    }

    checkDupicateMappings(selectedColumnValues, columnStepDetails) {
        const selectedColumns: number[] = [];
        let isDuplicate = false;
        Object.keys(selectedColumnValues).forEach((element) => {
            const selectedValue = selectedColumnValues[element];
            const selectedField = columnStepDetails.filter(
                (item) => item.propertyName === element
            )[0];
            selectedField.isError = false;
            if (selectedValue !== '0') {
                if (selectedColumns.indexOf(Number(selectedValue)) >= 0) {
                    selectedField.isError = true;
                    this.showToastWithError(
                        'Please check your mappings as same field is mapped multiple times.'
                    );
                    isDuplicate = true;
                    return isDuplicate;
                } else {
                    selectedColumns.push(Number(selectedValue));
                }
            }
        });
        return isDuplicate;
    }

    initializeSaveSearch(query, typeOfSave) {
        query.page = 1;
        query.request_type = typeOfSave === 'contact' ? 'CONTACTS' : 'COMPANIES';

        if (typeof query.tiers === 'object') {
            if (query.tiers['humanverified']) {
                query.tiers = '0';
            } else {
                query.tiers = this.auth.getPendingTiers().toString();
            }
            if (query['overrideTiers']) {
                delete query.overrideTiers;
            }
            query['company_min_size'] = '';
            query['company_max_size'] = '';
            query['company_naics_codes'] = '';
        }
        if (typeOfSave.toLowerCase() === 'contact') {
            if (query.job_levels) {
                if (!query['job_levels']['other'] || query['job_levels']['other']) {
                    delete query['job_levels']['other'];
                }
            }

            if (!query['sort_by']) {
                // 4438
                query.sort_by = 'contact_score';
                query.sort_direction = 'asc';
            }

            if (!query['gte_last_modified_date']) {
                query['gte_last_modified_date'] = '';
            }
            if (!query['location_types']) {
                query['location_types'] = [];
            }
        }
        if (query.company_location_hq === undefined) {
            query['company_location_hq'] = true;
            query['company_location_branch'] = true;
        }

        if (query.is_international === null || !query?.hasOwnProperty('is_international')) {
            query['is_international'] = null;
        } else if (query.is_international === 0) {
            query['is_international'] = 0;
        }
        if (!query.location_country_codes) {
            query['location_country_codes'] = ['US'];
        } else {
            if (
                query['location_country_codes'].length === 0 &&
                query.hasOwnProperty('is_international') && query.is_international !== null && query.is_international !== 1
            ) {
                query['location_country_codes'] = ['US'];
            }
        }
        if (typeOfSave.toLowerCase() === 'company') {
            if (!query['sort_by']) {
                // 4438
                query.sort_by = 'company_score';
                query.sort_direction = 'asc';
            }
            if (!query['contact_revenue_max_identifier']) {
                // 4438
                query.contact_revenue_max_identifier = 'M';
                query.contact_revenue_min_identifier = 'M';
            }
        }
        if (!query['rankings']) {
            query['rankings'] = CompanySearch.rankings;
        }
        // TODO: review logic, it is duplicated in multiple places
        const intentTopicsKey: string = this.getIntentTopicsKey(query);
        if (intentTopicsKey && query?.[intentTopicsKey]?.length > 0) {
            this.accountService.getAccountIntentTopics().pipe(take(1)).subscribe((topics : AccountIntentTopic[]) => {
                const yourTopics = cloneDeep(topics);
                const intenTopicIds = yourTopics.filter(yourTopic => query[intentTopicsKey].includes(yourTopic.topic_id)).map((selectedTopic) => selectedTopic.topic_id);
                if (intenTopicIds.length > 0) {
                    query[intentTopicsKey] = intenTopicIds;
                } else {
                    delete query[intentTopicsKey];
                }
            });
        }
        query = this.criteriaToRequestService.transformCSVToArray(query, [
            'company_domains',
            'company_names',
            'company_naics_codes',
            'company_sic_codes',
        ]);
        query.request_type =
            typeOfSave.toLowerCase() === 'company' ? 'COMPANIES' : 'CONTACTS';
        return query;
    }

    getIntentTopicsKey(obj: any): 'intent_topic_ids' | 'si_intent_topic_ids' {
        if (obj.hasOwnProperty('intent_topic_ids')) {
            return 'intent_topic_ids';
        }
        if (obj?.si_intent_topic_ids?.length) {
            return 'si_intent_topic_ids';
        }
        return null;
    }

    viewCompanyContacts(company: any, tiers: string) {
        let contactData;
        contactData = cloneDeep(this.searchModelData);
        this.saveSearch.universalActiveSearch = null;
        this.saveSearch.currentSearchId = '';
        if (tiers === 'human') {
            contactData.tiers = this.auth.getHumanVerifiedTiers().toString();
        } else {
            contactData.tiers = this.auth.getPendingTiers().toString();
        }
        contactData['company_ids'] = [
            { companyIds: [company.company_id], companyName: company.primary_name || company.company_name },
        ];
        contactData['page'] = 1;
        contactData['company_names'] = [company.primary_name || company.company_name];
        this.contactsService.contactLoadedFlag.next(true);
        this.saveSearch.welComeLoadedFlag.next(true);
        this.contactsService.resultPage.resultData = [];
        this.contactsService.resultPage.verifiedData = [];
        this.contactsService.resultPage.verifiedDataType = null;
        if (this.companyService.resultPage.resultData.length > 0) {
            this.companyService.resultPage.IsNavigating = true;
        } else {
            this.companyService.resultPage.IsNavigating = false;
        }
        if (this.contactsService.resultPage.resultData.length > 0) {
            this.contactsService.resultPage.IsNavigating = true;
        } else {
            this.contactsService.resultPage.IsNavigating = false;
        }
        if (
            window.history.state.type === 'company' &&
            this.companyService.resultPage &&
            this.companyService.resultPage.companySidebarData &&
            this.companyService.resultPage.companySidebarData.is_international === 1
        ) {
            contactData.is_international =
                this.companyService.resultPage.companySidebarData.is_international;
            contactData.location_country_codes =
                this.companyService.resultPage.companySidebarData.location_country_codes;
        } else {
            if (
                window.history.state.type === 'contact' &&
                this.contactsService.resultPage &&
                this.contactsService.resultPage.contactSidebarData &&
                this.contactsService.resultPage.contactSidebarData.is_international === 1
            ) {
                contactData.is_international =
                    this.contactsService.resultPage.contactSidebarData.is_international;
                contactData.location_country_codes =
                    this.contactsService.resultPage.contactSidebarData.location_country_codes;
            } else {
                contactData.is_international = 0;
            }
        }
        return contactData;
    }

    onChangeValue(value, data, modalValue) {
        if (value) {
            data.forEach((element) => {
                modalValue[element.key] = true;
            });
        } else {
            data.forEach((element) => {
                modalValue[element.key] = false;
            });
        }
    }

    onChangeLevelandDeratment(value) {
        if (value) {
            for (const prop in value) {
                if (value.hasOwnProperty(prop)) {
                    if (value[prop] === false) {
                        return false;
                    }
                }
            }
            return true;
        } else {
            return false;
        }
    }

    requestBodySubscription(contactData) {
        this.router.navigate(['results/contacts']);
        setTimeout(() => {
            this.requestBodyService.changeContactBodyData(contactData);
        }, 2000);
    }

    requestBodyUniversalSubscription(contactData) {
        //----------------
        // this.searchService.changeSelectedTab.next('CONTACTS');
        this.searchService.searchModelData.request_type = 'CONTACTS';
        localStorage.setItem('selectedType', 'CONTACTS');
        //----------------
        contactData['request_type'] = 'CONTACTS';
        // Start Fix for POR-649
        this.companyService.resultPage.companySidebarData = contactData;
        this.contactsService.resultPage.contactSidebarData = contactData;
        this.saveSearch.currentSearchId = '';
        this.saveSearch.universalActiveSearch = null;
        // End Fix for POR-649

        this.resultsService.changeTab(ResultTabsetUrlsEnum.SEARCH);
        setTimeout(() => {
            this.contactsService.contactLoadedFlag.next(true);
            this.searchService.universalCount = null;
            this.requestBodyService.changeContactBodyData(contactData);
        }, 500);
    }

    isNotEdgeBrowser(): boolean {
        return window.navigator.userAgent.indexOf('Edge') === -1;
    }

    getLogoURL(fullURL, domain) {
        return fullURL && fullURL.indexOf('https') > -1
            ? fullURL
            : 'https://logo.clearbit.com/' + domain;
        // return fullURL && (fullURL.indexOf('https') > -1) ? fullURL : '../../../../../assets/images/placeholder.png';
    }

    getStringLocalzation(term) {
        if (term) {
            return term.toLocaleString();
        }
    }

    onReportingDashboardSearch(searchData: UniversalSearchModelType): void {
        this.clearSidebarSearch();
        this.searchService.clearSearch();
        this.saveSearch.refreshList();
        this.companyService.resultPage.resultData = [];
        this.contactsService.resultPage.resultData = [];
        this.saveSearch.currentSearchName = '';
        this.saveSearch.currentCompanySearchId = '';
        this.saveSearch.companyActiveSearch = '';
        this.saveSearch.welComeLoadedCompanyFlag.next(true);
        this.saveSearch.emitExternalSavedSearchEvent();
        this.resultsService.changeTab(ResultTabsetUrlsEnum.SEARCH, true);

        if (searchData.request_type === REQUEST_TYPE.CONTACTS) {
            this.requestBodyService.changeContactBodyData(undefined);
            this.contactsService.contactLoadedFlag.next(true);
            this.searchService.changeSelectedTab.next('CONTACTS');
            setTimeout(() => {
                this.requestBodyService.changeContactBodyData(searchData);
            }, 1000);
        } else if (searchData.request_type === REQUEST_TYPE.COMPANIES) {
            this.requestBodyService.changeCompanyBodyData(undefined);
            this.companyService.companyLoadedFlag.next(true);
            this.searchService.changeSelectedTab.next('COMPANIES');
            setTimeout(() => {
                this.requestBodyService.changeCompanyBodyData(searchData);
            }, 1000);
        }

        this.churnZeroEvents('Portal - Company - Welcome Search');
    }

    onDashboardAssistedSearch(data: { assitedSearch: boolean, searchModelData: UniversalSearchModelType }): void {
        this.companyService.resultPage.resultData = [];
        this.contactsService.resultPage.resultData = [];
        this.searchService.universalCount = null;
        this.searchService.clearSearch();
        this.resultsService.changeTab(ResultTabsetUrlsEnum.SEARCH, true);
        if (data.searchModelData.request_type === REQUEST_TYPE.CONTACTS) {
            this.requestBodyService.changeContactBodyData(undefined);
            this.contactsService.contactLoadedFlag.next(true);
            this.requestBodyService.changeContactBodyData(data.searchModelData);
            data.assitedSearch ? this.churnZeroEvents('Portal - Contact - Assisted Search') : this.churnZeroEvents('Portal - Contact - Welcome Search')
            setTimeout(() => {
                this.requestBodyService.changeContactBodyData(data.searchModelData);
            }, 1000);
        } else if (data.searchModelData.request_type === REQUEST_TYPE.COMPANIES) {
            this.requestBodyService.changeCompanyBodyData(undefined);
            this.companyService.companyLoadedFlag.next(true);
            this.requestBodyService.changeCompanyBodyData(data.searchModelData);
            data.assitedSearch ? this.churnZeroEvents('Portal - Company - Assisted Search') : this.churnZeroEvents('Portal - Company - Welcome Search')
            setTimeout(() => {
                this.requestBodyService.changeCompanyBodyData(data.searchModelData);
            }, 1000);
        }
    }

    async applyCompaniesSearchByRequest(requestBodyCompanies): Promise<void> {
        this.saveSearch.currentSearchName = '';
        this.saveSearch.currentCompanySearchId = '';
        this.saveSearch.currentSearchId = '';
        this.saveSearch.companyActiveSearch = '';
        this.saveSearch.universalActiveSearch = null;
        this.saveSearch.welComeLoadedCompanyFlag.next(true);
        this.saveSearch.emitExternalSavedSearchEvent();
        this.companyService.resultPage.resultData = [];
        this.companyService.companyLoadedFlag.next(true);
        this.searchService.universalCount = null;
        this.searchService.searchModelData.request_type = 'COMPANIES';
        this.contactsService.resultPage.resultData = [];
        this.searchService.changeSelectedTab.next('COMPANIES');
        await this.resultsService.changeTab(ResultTabsetUrlsEnum.SEARCH, true);
        setTimeout(() => {
            this.requestBodyService.changeCompanyBodyData(requestBodyCompanies);
        }, 1000);
    }

    getRevenueValue(value) {
        let revenueValue;
        let revenuePrevifx;
        if (value) {
            if (value >= 1000) {
                revenueValue = value / 1000;
                if (revenueValue.toFixed(1).split('.')[1] === '0') {
                    revenueValue = revenueValue.toFixed(0);
                } else {
                    revenueValue = revenueValue.toFixed(1);
                }
                revenuePrevifx = 'B';
            } else {
                revenueValue = value;
                revenuePrevifx = 'M';
            }

            if (value !== 0) {
                revenueValue = parseFloat(revenueValue);
                return '$' + revenueValue.toLocaleString() + '' + revenuePrevifx;
            } else {
                return '';
            }
        }
    }

    getCurrentPreviousQuarter(value) {
        const today = new Date(),
            quarter = Math.floor(today.getMonth() / 3);
        let startDate, endDate;

        switch (value) {
            case 'previous':
                startDate = new Date(today.getFullYear(), quarter * 3 - 3, 1);
                endDate = new Date(
                    startDate.getFullYear(),
                    startDate.getMonth() + 3,
                    0
                );
                break;
            default:
                startDate = new Date(today.getFullYear(), quarter * 3, 1);
                endDate = new Date(
                    startDate.getFullYear(),
                    startDate.getMonth() + 3,
                    0
                );
                break;
        }

        return {
            StartDate: startDate,
            EndDate: endDate,
        };
    }

    getDate(date, dayCount) {
        return new Date(date.setDate(date.getDate() - dayCount));
    }

    setDateFormate(date) {
        return (
            date.getMonth() + 1 + '/' + date.getDate() + '/' + date.getFullYear()
        );
    }

    formatDateTime() {
        const currentDate = new Date();
        const day = currentDate.getDate();
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();
        let hours = currentDate.getHours();
        const minutes = currentDate.getMinutes();
        const ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        if (hours === 0) {
            hours = 12;
        }
        const hourStr = hours < 10 ? '0' + hours : hours;
        const minuteStr = minutes < 10 ? '0' + minutes : minutes;
        const dateTime = month.toString() + day.toString() + year.toString() + '_';
        const strDateTime = dateTime + hourStr + minuteStr + ampm.toString();
        return strDateTime;
    }

    csvCompanyExportAllWithoutAPI(response, listType, exportType, listName?) {
        const replacer = (key, value) => (value === null ? '' : value);
        const header = Object.keys(response[0]);
        const csv = response.map((row) =>
            header
            .map((fieldName) => JSON.stringify(row[fieldName], replacer))
            .join(',')
        );
        csv.unshift(header.join(','));
        const csvArray = csv.join('\r\n');
        const blob = new Blob([csvArray], { type: 'text/csv;charset=utf-8' });
        saveAs(
            blob,
            (listName ? listName + '_' : '') +
            listType +
            '_' +
            this.formatDateTime() +
            '.csv'
        );
    }

    exportCompanyAllCRM(params: ExportCompanyAllCrmParamsInterface) {
        if (params.selectedCompany?.length > params.maxCount) {
          this.showToastWithError(
            'You may only export up to ' +
            params.maxCount.toString() +
            ' companies at once.'
          );
          return;
        }

        this.savedExportFlowData = {
            method: 'exportCompanyAllCRM',
            params
        }
        this.ifFromList = params.fromExport;


        if (this.exportCompanyComponentFactoryService.isAllowExport(params.type)) {
            this.exportCompanyComponentFactoryService.exportOauthCRM(params, this.churnZero);
        } else if (params.type === 'companyCSV') {
            this.exportCsvPreviewAsync({
                type: params.type as 'companyCSV',
                title: 'Export Companies',
                fileName: params.fileName,
                ids: params.selectedCompany,
                requestBody: params.requestBody,
                totalCount: params.selectedCompany?.length,
                requestType: OfflineExportsExportTypesEnum.COMPANIES,
                bypassCsvNameModal: params.bypassCsvNameModal,
                skipAccuracyTierCheck: true,
                icpIntelJobUUID: params.icpIntelJobUUID,
                showIntentAutomationView: params.showIntentAutomationView
            });
            this.churnZeroEvents('Portal - Company - Export CSV');
        }
    }

    getSelectedAccuracyTier(rawSelectedContacts?, searchCriteria?): Array<'HV' | 'EV' | 'MV'> | null {
        const selectedContacts = rawSelectedContacts?.[0]?.verified !== undefined ? rawSelectedContacts : rawSelectedContacts?.matchIds;

        const accuracyTiers = [];

        if (!selectedContacts || (selectedContacts?.[0]?.verified === undefined && selectedContacts?.[0]?.isEvContact === undefined)) {
            const selectedTier = searchCriteria?.accuracy_tier_contact || this.searchService.searchCriteria?.accuracy_tier_contact;

            switch (selectedTier) {
                case 'MV':
                    accuracyTiers.push('HV', 'EV', 'MV');
                    break;
                case 'EV':
                    accuracyTiers.push('HV', 'EV');
                    break;
                case 'HV':
                    return null;
                default:
                    break;
            }

            return accuracyTiers;
        }

        const nonHumanVerified = selectedContacts.filter(x => !x.verified);
        if (!nonHumanVerified.length) {
            return null;
        } else {
            if (selectedContacts.length > nonHumanVerified.length) {
                accuracyTiers.push('HV');
            }
            const emailVerified = nonHumanVerified.filter(x => x.isEvContact);
            if (emailVerified.length) {
                accuracyTiers.push('EV');
            }
            const machineVerified = nonHumanVerified.filter(x => !x.isEvContact);
            if (machineVerified.length) {
                accuracyTiers.push('MV');
            }
        }

        return accuracyTiers;
    }

    getAccuracyTierExportText(accuracyTier: Array<'HV' | 'EV' | 'MV'> | null) {
        if (!accuracyTier || accuracyTier.length === 0) {
            return null;
        }

        const tiers = {
            'HV': 'human verified',
            'EV': 'email verified',
            'MV': 'machine verified'
        };

        const tierTexts = accuracyTier.map(tier => tiers[tier]);

        if (accuracyTier.includes('MV') && this.auth.user.user_meta.allowExportOfPendingVerificationContacts === '0') {
            return `Your export includes machine verified contacts which cannot be exported. These contacts must be email or human verified before they can be exported. All other contacts will be exported.`;
        }

        let tierText;
        if (tierTexts.length > 1) {
            const lastTier = tierTexts.pop();
            tierText = tierTexts.join(', ') + ' and ' + lastTier;
        } else {
            tierText = tierTexts[0];
        }

        return `Your export includes ${ tierText } contacts.`;
    }

    filterOutMvCheck(contactArrayToPass, isArrayOfStrings) {
        if (typeof contactArrayToPass[0] === 'string') {
            return contactArrayToPass;
        }

        if (contactArrayToPass?.[0]?.verified !== undefined) {
            const filteredContacts = contactArrayToPass.filter(x => x.verified || x.isEvContact);
            return isArrayOfStrings ? filteredContacts.map(x => x.matchId) : filteredContacts;
        } else if (contactArrayToPass?.matchIds?.[0]?.verified !== undefined) {
            const filteredContacts = contactArrayToPass.matchIds.filter(x => x.verified || x.isEvContact);
            contactArrayToPass.matchIds = isArrayOfStrings ? filteredContacts.map(x => x.matchId) : filteredContacts;
            return contactArrayToPass;
        } else {
            return contactArrayToPass;
        }
    }

    exportAllCRM(params: ExportAllCrmParamsInterface) {
        this.savedExportFlowData = {
            method: 'exportAllCRM',
            params
        }
        const exportFromListsRod = params.isExportKey === 'list' || params.isExportKey === 'rod';
        const creditRemaining = this.getCombinedRemainingCredits();
        const notExportedToMT =
          params.selectedContacts.length -
          (
            ['list', 'rod', 'fromInboxIntel', ''].includes(params.isExportKey) ?
            0 :
            params.selectedContacts.filter(x => x.purchaseHistory?.isExportedToMT === 1).length
          );
        if (
            params.type === 'Preview' ||
            exportFromListsRod ||
            params.selectedContacts.length - params.revealedContacts?.length <=
            creditRemaining ||
            notExportedToMT <= creditRemaining
        ) {
            let selectedContacts = Array.isArray(params.selectedContacts)
                ? params.selectedContacts.map((item) => {
                    if (item.matchId) {
                        return {
                        name: item.firstName + ' ' + item.lastName,
                        matchId: item.matchId,
                        companyName: item.companyName,
                        verified: item.verified,
                        isEvContact: item.isEvContact,
                        jobTitle: item.jobTitle,
                        primaryDomain: item.primaryDomain,
                        };
                    } else {
                        return item;
                    }
                    })
                : [];

            const isArrayOfStrings = typeof selectedContacts[0] === 'string';
            const contactArrayToPass = params.csvSelectedContacts?.length ? params.csvSelectedContacts : params.isExportKey === 'list' ? this.contactsService.matchIdsV2 : params.selectedContacts;
            const accuracyTier = this.getSelectedAccuracyTier(contactArrayToPass);

            if (this.auth.user.user_meta.allowExportOfPendingVerificationContacts === '0' && accuracyTier?.includes('MV')) {
                selectedContacts = this.filterOutMvCheck(contactArrayToPass, isArrayOfStrings);
            }

            const accuracyTierText = this.getAccuracyTierExportText(accuracyTier);
            const modalConfig: MatDialogConfig = {
                panelClass: ['modal-lg', LEGACY_THEME_CLASS],
                data: {
                  contacts: selectedContacts,
                  isExportKey: params.isExportKey,
                  accuracyTier,
                  accuracyTierText,
                  app: params.app,
                  requestBody: params.requestBody,
                  icpIntelJobUUID: params.icpIntelJobUUID,
                }
            };
            if (params.type.toLowerCase() === 'salesforce') {
                this.exportAllCrmSalesforce({ isExportKey: params.isExportKey, modalConfig, getSelectedContacts: params.selectedContacts, selectedContacts });
            } else if (params.type.toLowerCase() === 'hubspot') {
                this.exportAllCrmHubspot({ isExportKey: params.isExportKey, modalConfig, getSelectedContacts: params.selectedContacts, selectedContacts });
            } else if (params.type.toLowerCase() === 'outreach') {
                if (environment.outReachMaxCount >= params.selectedContacts.length) {
                    if (this.auth.hasEmail('orEmail')) {
                        this.matDialog.open(
                            OutreachComponent,
                            modalConfig
                        );
                    } else {
                        this.openOauthCRM(
                            OauthProvider.Outreach,
                            selectedContacts,
                            params.isExportKey
                        );
                    }
                } else {
                    this.toastr.warning(
                        'You may only export up to ' +
                        environment.outReachMaxCount.toLocaleString() +
                        ' contacts at once.'
                    );
                }
                this.churnZeroEvents('Portal - Contact - Export Outreach');
            } else if (params.type.toLowerCase() === 'salesloft') {
                if (environment.salesLoftMaxCount >= params.selectedContacts.length) {
                    if (this.auth.hasEmail('slEmail')) {
                        this.matDialog.open(
                            SalesloftComponent,
                            modalConfig
                        );
                    } else {
                        this.openOauthCRM(
                            OauthProvider.SalesLoft,
                            selectedContacts,
                            params.isExportKey
                        );
                    }
                } else {
                    this.toastr.warning(
                        'You may only export up to ' +
                        environment.salesLoftMaxCount.toLocaleString() +
                        ' contacts at once.'
                    );
                }

                this.churnZeroEvents('Portal - Contact - Export SalesLoft');
            } else if (params.type === 'Marketo') {
                if (environment.marketoMaxCount >= params.selectedContacts.length) {
                    if (this.auth.hasEmail('mktUser')) {
                        this.matDialog.open(
                            MarketoComponent,
                            modalConfig
                        );
                    } else {
                        this.routableModalsStateService.navigateWithData({ commands: ['/', { outlets: { modal: [ModalNavigationEnum.MARKETO] } }] }, {
                            contacts: selectedContacts
                        });
                    }
                } else {
                    this.toastr.warning(
                        'You may only export up to ' +
                        environment.marketoMaxCount.toLocaleString() +
                        ' contacts at once.'
                    );
                }
                this.churnZeroEvents('Portal - Contact - Export Marketo');
            } else if (params.type === 'Dynamics') {
                if (environment.salesforceMaxCount >= params.selectedContacts.length) {
                    if (this.auth.hasEmail('dyEmail')) {
                        this.matDialog.open(
                            DynamicsComponent,
                            modalConfig
                        );
                    } else {
                        this.openOauthCRM(
                            OauthProvider.Dynamics,
                            selectedContacts,
                            params.isExportKey
                        );
                    }
                } else {
                    this.toastr.warning(
                        'You may only export up to ' +
                        environment.salesforceMaxCount.toLocaleString() +
                        ' contacts at once.'
                    );
                }
                this.churnZeroEvents('Portal - Contact - Export Dynamics');
            } else if (params.type === 'Zoho') {
                if (environment.salesforceMaxCount >= params.selectedContacts.length) {
                    if (this.auth.hasEmail('zhEmail')) {
                        this.matDialog.open(ZohoComponent, modalConfig);
                    } else {
                        this.openOauthCRM(
                            OauthProvider.Zoho,
                            selectedContacts,
                            params.isExportKey
                        );
                    }
                } else {
                    this.toastr.warning(
                        'You may only export up to ' +
                        environment.salesforceMaxCount.toLocaleString() +
                        ' contacts at once.'
                    );
                }
                this.churnZeroEvents('Portal - Contact - Export Zoho');
            } else if (params.type.includes('custom')) {
                this.matDialog.open(
                    AccountExportAppWizardComponent,
                    modalConfig
                );
            } else if (params.type === 'CSV' || params.type === 'Preview') {
                const mConfig: MatDialogConfig = {
                    panelClass: 'modal-md',
                    data: {
                        title: 'Export Contacts',
                        exportMessage:
                            'This export can take up to 5 minutes depending on the size of the export',
                        icpIntelJobUUID: params.icpIntelJobUUID,
                    },
                };
                const preview = params.type === 'CSV' ? 0 : 1;
                if (params.isExportKey === 'fromInboxIntel') {
                    const hvContacts = [];
                    const mvContacts = [];
                    const unContacts = [];
                    const dialogRef: MatDialogRef<any> = this.matDialog.open(
                        PromptModalComponent,
                        mConfig
                    );
                    selectedContacts.map((item) => {
                        if (item['searchResult'] && item['searchResult'].matchId) {
                            if (item['searchResult'].verified) {
                                hvContacts.push(item['searchResult'].matchId);
                            } else {
                                mvContacts.push(item['searchResult'].matchId);
                            }
                        } else {
                            unContacts.push(item.inboxIntelContactId);
                        }
                    });
                    this.contactsService
                    .exportAllContactInboxIntel(
                        hvContacts,
                        mvContacts,
                        unContacts,
                        preview
                    )
                    .subscribe(
                        (response: any) => {
                            this.exportAllSuccessHandler(response, params.fileName, dialogRef);
                            const matchIds = selectedContacts
                            .filter((element) => element.searchResult.matchId)
                            .map((element) => element.searchResult.matchId);
                            this.requestBodyService.changeRevealMatchesID({
                                type: 'export' + params.type,
                                data: matchIds,
                            });
                        },
                        (error) => {
                            dialogRef?.close();
                            this.setRevealError(error.status, error.message);
                        }
                    );
                } else {
                    this.exportCsvPreviewAsync({
                        type: params.type as any,
                        ids: selectedContacts,
                        fileName: params.fileName,
                        requestBody: params.requestBody,
                        requestType: OfflineExportsExportTypesEnum.CONTACTS,
                        bypassCsvNameModal: params.bypassCsvNameModal,
                        accuracyTier,
                        accuracyTierText,
                        skipAccuracyTierCheck: true,
                    });
                }
                this.churnZeroEvents('Portal - Contact - Export' + ' ' + params.type);
            }
        } else {
            this.setRevealError(402, '');
        }
    }

    exportAllCrmHubspot({ isExportKey, modalConfig, getSelectedContacts, selectedContacts, totalCount }: {
        isExportKey: string,
        modalConfig: MatDialogConfig,
        totalCount?: number,
        getSelectedContacts?: any,
        selectedContacts?: any,
    }): void {
        this.featureFlagService.getFlag(FeatureFlagsEnum.HubspotLargeExports)
            .pipe(take(1))
            .subscribe((isHubspotLargeExportsEnabled: boolean) => {
                const hubspotLimit: number = isHubspotLargeExportsEnabled ? this.auth.getMaxAsyncExports() : environment.hubSpotMaxCount;

                if (hubspotLimit >= (totalCount || getSelectedContacts?.length)) {
                    if (this.auth.hasEmail('hsEmail')) {
                        this.matDialog.open(
                            HubspotComponent,
                            modalConfig
                        );
                    } else {
                        this.openOauthCRM(
                            OauthProvider.Hubspot,
                            selectedContacts,
                            isExportKey
                        );
                    }
                } else {
                    this.toastr.warning(`You may only export up to ${hubspotLimit.toLocaleString()} contacts at once.`);
                }
                this.churnZeroEvents('Portal - Contact - Export Hubspot');
            })
    }

    exportAllCrmSalesforce({ isExportKey, modalConfig, getSelectedContacts, selectedContacts, totalCount }: {
        isExportKey: string,
        modalConfig: MatDialogConfig,
        totalCount?: number,
        getSelectedContacts?: any,
        selectedContacts?: any
    }): void {
        this.featureFlagService.getFlag(FeatureFlagsEnum.SalesforceIntegrationsForLargeExports)
            .pipe(take(1))
            .subscribe((isSalesforceFeatureEnabled: boolean) => {
                const salesforceLimit: number = isSalesforceFeatureEnabled ? this.auth.getMaxAsyncExports() : environment.salesforceMaxCount;

                if (salesforceLimit >= (totalCount || getSelectedContacts?.length)) {
                    if (this.auth.hasEmail('sfEmail') || isExportKey === 'fromInboxIntel') {
                        const panelClass: string[] = [
                            isExportKey === 'list' ? 'modal-lg' : 'modal-xl',
                            LEGACY_THEME_CLASS,
                        ];

                        this.matDialog.open(SalesforceComponent, {
                            ...modalConfig,
                            panelClass,
                        });
                    } else {
                        this.salesforceService.isFromInboxIntel = this.auth.hasEmail('sfEmail') && isExportKey === 'fromInboxIntel';
                        this.openOauthCRM(OauthProvider.Salesforce, selectedContacts, isExportKey);
                    }
                } else {
                    this.toastr.warning(`You may only export up to ${salesforceLimit.toLocaleString()} contacts at once.`);
                }
                this.churnZeroEvents('Portal - Contact - Export Salesforce');
            })
    }

    exportCsvWebhooks({ ids, requestBody, request_type, webhookId, jobContactsId }: {
        ids?: string[];
        requestBody?: OfflineExportsExportRequestDto;
        request_type?: OfflineExportsExportTypesEnum | REQUEST_TYPE;
        webhookId: string;
        webhookRequestBody: WebhookRequestBody;
        jobContactsId: string;
    }) {
        let company_ids: string[] = [];
        if (REQUEST_TYPE.COMPANIES) {
            company_ids = ids?.filter(Boolean);
        }

        const match_ids: string[] = ids?.filter(Boolean);
        const request: any = requestBody ? requestBody : {};
        const newRe = this.searchService.criteriaToRequest({
            request_type,
            ...(match_ids?.length | company_ids?.length
                ? {
                    match_ids,
                    company_ids,
                    verified: request.verified,
                    tiers: request.tiers,
                }
                : request),
        });

        return this.offlineExportsApiService.exportCsvWebhooks(webhookId, {
            label: this.getFileNameByRequestType(request_type),
            ...newRe,
        }, jobContactsId);
    }

    exportCsvPreviewAsync(params: ExportCsvPreviewParamsInterface): void {
        // TODO: refactor with HS async exports
        if (!params.skipAccuracyTierCheck) {
            params.accuracyTier = params.accuracyTier ? params.accuracyTier : this.getSelectedAccuracyTier(params.ids, params.requestBody);
            params.accuracyTierText = params.accuracyTierText ? params.accuracyTierText : this.getAccuracyTierExportText(params.accuracyTier);
        }

        const mConfig: MatDialogConfig = {
            panelClass: 'modal-md',
            data: {
                title: params.title || 'Export Contacts',
                labelText: 'File Name',
                saveButtonText: 'Submit',
                closeButtonText: 'Close',
                inputValue: params.fileName || this.getFileNameByRequestType(params.requestType),
                inputValidators: [
                    Validators.pattern('^[a-zA-Z0-9\\s\\.?!,@&_-]+$'),
                    Validators.maxLength(100),
                ],
                mandatoryInput: true,
                isContactCsvExport: true,
                exportMessage: 'This export can take up to 5 minutes depending on the size of the export',
                ...(params.type !== 'companyCSV' ? {
                  accuracyTier: params.accuracyTier,
                  accuracyTierText: params.accuracyTierText,
                } : {}),
                icpIntelJobUUID: params.icpIntelJobUUID
            }
        };
        const preview: number = params.type === 'Preview' ? 1 : 0;
        const count: number = params.ids?.length || params.totalCount || 0;
        const exportingItemName: string =
            params.type === 'companyCSV' ? 'companies' : 'contacts';

        // CSV IN BROWSER
        // less than 5k contacts and companies (allowed; receive csv in the browser) or
        // no asyncexports access and csvLargeExportAllowed and contacts less than 10K
        if (this.hasAccessExportInBrowser(count)) {
            let fileNameCall$ = of({ success: true, inputValue: params.fileName });
            let dialogRef: MatDialogRef<PromptModalComponent>;

            if (typeof params.bypassCsvNameModal === 'boolean'
                ? !params.bypassCsvNameModal
                : !params.fileName) {
                dialogRef = this.matDialog.open(
                    PromptModalComponent,
                    mConfig
                );
                fileNameCall$ = dialogRef.afterClosed();
            }

            const fileNameResponse$ = fileNameCall$.pipe(take(1), filter((response: any) => response?.success));

            if (params.type === 'companyCSV') {
                fileNameResponse$
                .pipe(
                    switchMap(({ inputValue }: any) =>
                        this.companyService
                        .exportAllCompany(params.ids, params.requestBody, params.icpIntelJobUUID, params.showIntentAutomationView)
                        .pipe(map((response) => [response, inputValue]))
                    )
                )
                .subscribe(
                    ([response, inputValue]: [any, string]) => {
                        this.exportAllSuccessHandler(response, inputValue, dialogRef);
                        this.auth.updateLoggedUserData();
                        this.requestBodyService.changeCompanyRevealMatchesID({
                            type: 'exportCSV',
                            data: params.ids,
                        });
                    },
                    (error) => {
                        dialogRef?.close();
                        this.setRevealError(error.status, error.message);
                    }
                );
                return;
            }

            fileNameResponse$
            .pipe(
                switchMap(({ inputValue }: any) =>
                    this.contactsService
                    .exportAllContact(params.ids, preview)
                    .pipe(map((response) => [response, inputValue]))
                )
            )
            .subscribe(
                ([response, inputValue]: [any, string]) => {
                    this.exportAllSuccessHandler(response, inputValue, dialogRef);
                    if (count < environment.maxExports) {
                        this.requestBodyService.changeRevealMatchesID({
                            type: 'export' + params.type,
                            data: params.ids,
                        });
                    }
                },
                (error) => {
                    dialogRef?.close();
                    this.setRevealError(error.status, error.message);
                }
            );
            return;
        }

        // CSV WITH ASYNC PROCESS
        // more than 5K contacts if user has asyncexports access
        if (this.userService.hasAccess(AccountAttributesEnum.ASYNCEXPORTS)) {
            if (count > this.auth.getMaxAsyncExports()) {
                this.toastr.warning(
                    `You may only export up to ${ this.auth
                    .getMaxAsyncExports()
                    .toLocaleString() } ${ exportingItemName } at once.`
                );
                return;
            }

            if (typeof params.type === 'object') {
                const modalConfig: MatDialogConfig = {
                    panelClass: 'modal-md',
                    data: {
                        app: params.type,
                        requestBody: params.requestBody,
                        itemsCount: count,
                    },
                };
                this.matDialog.open(
                    AccountExportAppWizardComponent,
                    modalConfig
                );
            } else {
                const fileNameResponse$ = (
                    typeof params.bypassCsvNameModal === 'boolean'
                        ? !params.bypassCsvNameModal
                        : !params.fileName
                )
                    ? this.matDialog.open(PromptModalComponent, {
                        ...mConfig,
                        data: {
                            ...mConfig.data,
                            labelText: 'Job Name',
                            exportMessage:
                                'Due to the size of your request, you will receive an email when your export is ready. This may take 30 minutes or more during busy times.',
                        },
                    }).afterClosed()
                    : of({ success: true, inputValue: params.fileName });

                fileNameResponse$
                .pipe(
                    take(1),
                    filter((response: any) => response?.success),
                    switchMap((response: any) => {
                        const match_ids: string[] = params.ids?.filter(Boolean);
                        const request: any = params.requestBody || {};
                        const newRe = this.convertMatchIdsToRequest(request, match_ids, { request_type: params.requestType, is_intent_automation_from_email: params.showIntentAutomationView });
                        return this.offlineExportsApiService.exportCsv(
                            {
                                label: response.inputValue,
                                ...newRe
                            },
                            Boolean(preview)
                        );
                    }),
                    tap(() => this.toastr.success('Requested successfully!')),
                    catchError((error) => {
                        this.setRevealError(error.status, error.message);
                        return of();
                    })
                )
                .subscribe();
            }
        }

        if (!this.userService.hasAccess(AccountAttributesEnum.ASYNCEXPORTS)) {
            this.toastr.warning(
                `You may only export up to ${ this.auth
                .csvExportLimit()
                .toLocaleString() } ${ exportingItemName } at once.`
            );
        }
    }

    hasAccessExportInBrowser(count: number): boolean {
        return (
            count < environment.maxExports ||
            (!this.userService.hasAccess(AccountAttributesEnum.ASYNCEXPORTS) &&
                this.auth.hasCsvLargeExportAllowed() &&
                count < environment.maxXLExports)
        );
    }

    convertMatchIdsToRequest(request, match_ids, additional = {}) {
        return this.searchService.criteriaToRequest({
            ...(match_ids?.length
                ? {
                    match_ids: match_ids.map((id) => {
                        if (typeof id === 'object') {
                            return id.matchId;
                        } else {
                            return id;
                        }
                    }),
                    verified: request?.verified,
                    tiers: request?.tiers,
                    accuracy_tier_contact: request?.accuracy_tier_contact !== '' ? request?.accuracy_tier_contact : undefined,
                } : request),
            ...additional,
        });
    }

    openOauthCRM(provider, matchId, isFromList?) {
        this.oauthService.open(provider, matchId, isFromList);
    }

    performGoogleAuthentication() {
        this.oauthService.open(OauthProvider.Google, [], 'google');
    }

    performMicrosoftAuthentication() {
        this.oauthService.open(OauthProvider.Microsoft, [], 'microsoft');
    }

    public isExportButtonGroupDisable(crmArray) {
        return (
            difference(crmArray, this.loggedInUserData.account_attributes).length >=
            crmArray.length
        );
    }

    public assignSettingsValue(actualArray, value, fieldName, compareValue) {
        if (value) {
            const retrunValue = actualArray.filter(
                (item) => item[compareValue] === value
            );
            if (retrunValue.length > 0) {
                return retrunValue[0][fieldName];
            } else {
                return undefined;
            }
        } else {
            return undefined;
        }
    }

    public showToastWithSuccess(
        message: string,
        title?: string,
        timeout?: number
    ) {
        this.toastr.success(message, title, {
            timeOut: timeout === undefined ? 5000 : timeout,
        });
    }

    public showToastWithWarning(
        message: string,
        title?: string,
        timeout?: number
    ) {
        this.toastr.warning(message, title, {
            timeOut: timeout === undefined ? 5000 : timeout,
        });
    }

    public showToastWithError(message: string, timeout: number = 5000) {
        if (this.networkManager.isOnline) {
            let errorMessage: string = message;
            if (!errorMessage || errorMessage.length === 0) {
                errorMessage = 'Undefined error.';
            }

            if (errorMessage !== 'Success') {
                this.toastr.error(errorMessage, 'Error', {
                    timeOut: timeout,
                });
            }
        } else {
            this.toastr.error(
                'Something went wrong. Ensure you are online and then refresh your browser to retry.',
                'Error',
                {
                    timeOut: timeout,
                }
            );
        }
    }

    displayExhaustedRodMessage() {
        this.toastr.error(
            `You have exhausted your RoD credits. Please contact <a target='_blank' href='https://support.salesintel.io/hc/en-us/requests/new'>support@salesintel.io</a>.`
        );
    }

    onFocusTooltip(event) {
        document.getElementById('brand-logo').focus();
        event.stopPropagation();
    }

    extractHostname(url): string {
        let hostname;
        // find & remove protocol (http, ftp, etc.) and get hostname

        if (url.indexOf('//') > -1) {
            hostname = url.split('/')[2];
        } else {
            hostname = url.split('/')[0];
        }

        // find & remove port number
        hostname = hostname.split(':')[0];
        // find & remove '?'
        hostname = hostname.split('?')[0];
        return hostname;
    }

    extractRootDomain(url): string {
        let domain = this.extractHostname(url);
        const splitArr = domain.split('.');
        const arrLen = splitArr.length;
        // extracting the root domain here
        // if there is a subdomain
        if (arrLen > 2) {
            domain = splitArr[arrLen - 2] + '.' + splitArr[arrLen - 1];
            // check to see if it's using a Country Code Top Level Domain (ccTLD) (i.e. '.me.uk')
            if (splitArr[arrLen - 1].length === 2) {
                // this is using a ccTLD
                domain = splitArr[arrLen - 3] + '.' + domain;
            }
        }
        domain = domain.toLowerCase().trim();
        return domain;
    }

    getQuerySaveSearch(id, typeOfSave) {
        this.saveSearch.getSavedSearchById(String(id)).subscribe(
            (successData) => {
                this.handleSearch(successData, typeOfSave);
            },
            (error) => {
                const deletedError = getErrorMessage(error);
                if (deletedError?.includes('deleted')) {
                    this.clearSidebarSearch();
                    this.searchService.clearSearch();
                    this.saveSearch.refreshList();
                }
            }
        );
    }

    handleSearch(
        successData,
        typeOfSave: 'company' | 'contact',
        isAutomationAlertView?: boolean
    ) {
        const query = this.initializeSaveSearch(successData.query, typeOfSave);
        //-----
        if (query.is_international===0) delete query.location_country_codes;
        if (query.is_international===1) {
            query.location_country_codes = query.location_country_codes.filter((code) => code !== 'US');
        }
        this.saveSearch.emitExternalSavedSearchEvent();
        //-----
        query.request_type = typeOfSave === 'contact' ? 'CONTACTS' : 'COMPANIES';
        this.requestBodyService.changeCompanyBodyData(undefined);
        this.requestBodyService.changeContactBodyData(undefined);
        if (typeOfSave?.toLowerCase() === 'contact') {
            query['tiers'] = '0';
            this.contactsService.contactLoadedFlag.next(true);
            this.companyService.companyLoadedFlag.next(true);
            this.saveSearch.setUniversalActiveSearch(successData);
            this.requestBodyService.changeContactBodyData(query);
            this.saveSearch.welComeLoadedFlag.next(true);
            this.router.navigate([`results/search`]);
        } else {
            query['verified'] = 1;
            this.companyService.companyLoadedFlag.next(true);
            this.contactsService.contactLoadedFlag.next(true);
            this.saveSearch.setUniversalActiveSearch(successData);
            this.requestBodyService.changeCompanyBodyData(query);
            this.saveSearch.welComeLoadedCompanyFlag.next(true);
            this.router.navigate([`results/search`]);
        }

        if (isAutomationAlertView) {
            this.isAutomationAlertViewSubject.next(isAutomationAlertView);
        }
    }

    private getFileName(response: any, fileName?: string): string {
        if (!fileName && fileName === undefined) {
            return response.headers
            .get('Content-Disposition')
            .split('attachment; filename=')[1];
        }
        return fileName + '.csv';
    }

    private exportAllSuccessHandler(response: any, fileName?: string, dialogRef?: MatDialogRef<any>): void {
        fileName = this.getFileName(response, fileName);
        const blob = new Blob([response.body], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, fileName);
        dialogRef?.close();
    }

    getCombinedRemainingCredits() {
        const combinedCredit = this.loggedInUserData.service_credits.find(
            (element) => element.credit_type === 'Combined'
        );
        return (
            combinedCredit?.credits_remaining +
            combinedCredit?.license_credit_limit -
            combinedCredit?.license_credits_used || 0
        );
    }

    getRodRemainingCredits() {
        const rodCredit = this.loggedInUserData.service_credits.find(
            (element) => element.credit_type === 'RoD'
        );
        return (
            rodCredit?.credits_remaining +
            (this.loggedInUserData.has_licence_credits
                ? rodCredit?.license_credit_limit - rodCredit?.license_credits_used
                : 0) || 0
        );
    }

    getRodCreditsUsed() {
        const rodCredit = this.loggedInUserData.service_credits.find(
            (element) => element.credit_type === 'RoD'
        );
        return (
            rodCredit?.credits_used +
            (this.loggedInUserData.has_licence_credits
                ? rodCredit?.license_credits_used
                : 0) || 0
        );
    }

    getRodCreditLimit() {
        const rodCredit = this.loggedInUserData.service_credits.find(
            (element) => element.credit_type === 'RoD'
        );
        return (
            rodCredit?.total_credit_limit +
            (this.loggedInUserData.has_licence_credits
                ? rodCredit?.license_credit_limit
                : 0) || 0
        );
    }

    exportVIDataToCsv(response: any, fileName: string) {
        this.exportAllSuccessHandler(response, fileName);
    }

    removeMatchId(toBeExported, matchData, type = 'contact') {
        let matchId;
        if (typeof matchData === 'object') {
            matchId = type === 'contact' ? matchData.match_id || matchData.matchId : matchData.company_id || matchData.companyId;
        } else {
            matchId = matchData;
        }

        if (typeof toBeExported[0] === 'object') {
            toBeExported.splice(toBeExported.map(obj => obj.id).indexOf(matchId), 1);
        } else {
            toBeExported.splice(toBeExported.indexOf(matchId), 1);
        }
    }

    getFileNameByRequestType(request_type: string): string {
        return [request_type.toLowerCase(), this.formatDateTime()].join('_');
    }

    getAndApplyActiveSavedSearchWithExtraParams(data:  Record<string, object>) {
        this.clearSidebarSearch();
        this.searchService.clearSearch();
        this.saveSearch.refreshList();
        this.companyService.resultPage.resultData = [];
        this.contactsService.resultPage.resultData = [];
        this.resultsService.changeTab(ResultTabsetUrlsEnum.SEARCH, true);
        this.saveSearch.getSavedSearchById(data.saved_search_id).pipe().subscribe((saveSearchsData) => {
            this.saveSearch.currentSearchId = saveSearchsData.id.toString();
            this.saveSearch.currentSearchName = saveSearchsData.searchName;
            const query = {
                ...this.initializeSaveSearch(saveSearchsData.query, REQUEST_TYPE.COMPANIES),
                verified: 1,
                ...data.paramsToUpdate,
                request_type: REQUEST_TYPE.COMPANIES
            }
            this.saveSearch.setUniversalActiveSearch(saveSearchsData);
            this.requestBodyService.changeCompanyBodyData(undefined);
            this.companyService.companyLoadedFlag.next(true);
            setTimeout(() => {
                this.saveSearch.emitExternalSavedSearchEvent();
                this.requestBodyService.changeCompanyBodyData(query);
            }, 1000);
        })
    }

    getDateParts(date: Date): Record<Intl.DateTimeFormatPartTypes, number> {
      return this.dateFormatter.formatToParts(date).reduce(
        (acc, item) => ({...acc, [item.type]: item.value}),
        {} as Record<Intl.DateTimeFormatPartTypes, number>
      );
    }
}
